import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import Iconify from 'src/components/iconify';
// auth
import { useAuthContext } from 'src/auth/hooks';
// api
import { useGetPatient } from 'src/api/patients';
import { useGetAIProcessingResults } from 'src/api/ai-processing';
import { getClaimFlowAccess } from 'src/utils/claim-flow-access';

// ----------------------------------------------------------------------

const navIcon = (name) => <Iconify icon={name} width={22} />;

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();

  const patientId = typeof window !== 'undefined' ? sessionStorage.getItem('patientToken') : null;
  const { patient } = useGetPatient(patientId);
  const claimId = patient?.claimId;
  const { results: aiResults } = useGetAIProcessingResults(claimId);

  const isAdmin = user?.permissions?.includes('admin');
  const isCfo = user?.permissions?.includes('cfo');

  return useMemo(() => {
    // Determine step accessibility
    const { hasPatient, hasDocuments, hasPolicyCoverageStep, isApproved } = getClaimFlowAccess({
      patientId,
      patient,
      aiResults,
    });
    const aiVerificationUnlockKey = patientId ? `claim.aiVerificationUnlocked:${patientId}` : null;
    const approvalWorkflowUnlockKey = patientId
      ? `claim.approvalWorkflowUnlocked:${patientId}`
      : null;
    const isAiVerificationUnlocked = Boolean(
      aiVerificationUnlockKey && sessionStorage.getItem(aiVerificationUnlockKey) === 'true'
    );
    const isApprovalWorkflowUnlocked = Boolean(
      approvalWorkflowUnlockKey && sessionStorage.getItem(approvalWorkflowUnlockKey) === 'true'
    );
    const canAccessAiVerification = hasPolicyCoverageStep && isAiVerificationUnlocked;
    const hasApprovalWorkflow = canAccessAiVerification && isApprovalWorkflowUnlocked;

    // Full menu for Admin
    if (isAdmin) {
      return [
        {
          items: [
            // Use the dashboard root so it is selected on initial load (/dashboard)
            {
              title: t('Dashboard'),
              path: paths.dashboard.root,
              icon: navIcon('eva:home-outline'),
            },
            {
              title: t('All Patients'),
              path: paths.dashboard.claim.allPatients,
              icon: navIcon('eva:people-outline'),
              disabled: false,
            },
            {
              title: t('All Claims'),
              path: paths.dashboard.claim.claims,
              icon: navIcon('eva:list-outline'),
              disabled: false,
            },
            {
              title: t('Patient Registration'),
              path: paths.dashboard.claim.patientRegistration,
              icon: navIcon('eva:person-outline'),
              disabled: false,
            },
            {
              title: t('Document Collection'),
              path: paths.dashboard.claim.documentCollection,
              icon: navIcon('eva:file-text-outline'),
              disabled: !hasPatient,
            },
            {
              title: t('AI Processing'),
              path: paths.dashboard.claim.aiProcessing,
              icon: navIcon('eva:activity-outline'),
              disabled: !hasDocuments,
            },
            {
              title: t('Policy coverage'),
              path: paths.dashboard.claim.policyCoverage,
              icon: navIcon('eva:book-open-outline'),
              disabled: !hasPolicyCoverageStep,
            },
            {
              title: t('AI Verification'),
              path: paths.dashboard.claim.aiVerification,
              icon: navIcon('eva:checkmark-square-2-outline'),
              disabled: !hasPolicyCoverageStep,
            },
            {
              title: t('Approval Workflow'),
              path: paths.dashboard.claim.approvalWorkflow,
              icon: navIcon('eva:trending-up-outline'),
              disabled: !hasPolicyCoverageStep,
            },
            {
              title: t('Finance'),
              path: paths.dashboard.claim.finance,
              icon: navIcon('eva:credit-card-outline'),
              disabled: !isApproved,
            },
            {
              title: t('Claim Closure'),
              path: paths.dashboard.claim.claimClosure,
              icon: navIcon('eva:archive-outline'),
              disabled: !isApproved,
            },
            // {
            //   title: t('Communications'),
            //   path: paths.dashboard.claim.communications,
            //   icon: navIcon('eva:bell-outline'),
            //   disabled: !isApproved,
            // },
          ],
        },
      ];
    }

    if (isCfo) {
      return [
        {
          items: [
            {
              title: t('CFO Receipts'),
              path: paths.dashboard.claim.cfoReceipts,
              icon: navIcon('eva:file-add-outline'),
              disabled: false,
            },
          ],
        },
      ];
    }

    // Limited menu for other roles (Doctor, Manager, etc.)
    return [
      {
        items: [
          { title: t('Dashboard'), path: paths.dashboard.root, icon: navIcon('eva:home-outline') },
        ],
      },
    ];
  }, [t, isAdmin, isCfo, patientId, patient, aiResults]);
}
